<script setup>
import dayjs from 'dayjs'
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { chatDraftKindEnum } from '@/stores/chat.store'
import { signalToneColor, signalToneEnum } from '@/composables/dashboard-kpi'
import { searchResultTypeEnum, useWebSiteStore } from '@/stores/web-site.store'

const draftKindConfig = Object.freeze({
  [chatDraftKindEnum.PROJECT]: {
    resultType: searchResultTypeEnum.PROJECT,
    openLabel: 'chat.openProjectForm',
    createdLabel: 'chat.projectCreated',
    icon: 'mdi-briefcase-plus-outline',
  },
  [chatDraftKindEnum.QUOTE]: {
    resultType: searchResultTypeEnum.QUOTE,
    openLabel: 'chat.openQuoteForm',
    createdLabel: 'chat.quoteCreated',
    icon: 'mdi-file-document-plus-outline',
  },
})

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
})
defineEmits(['open'])

const i18n = getI18n()
const webSiteStore = useWebSiteStore()

const draft = computed(() => props.message.draft)
const config = computed(() => draftKindConfig[draft.value.kind])
const contact = computed(() => [draft.value.contact_name, draft.value.contact_email, draft.value.contact_phone].filter(Boolean).join(' · '))
const period = computed(() => [draft.value.start_date, draft.value.end_date].filter(Boolean).map(date => dayjs(date).format('L')).join(' – '))
const unavailableCount = computed(() => draft.value.services.filter(service => service.availability && !service.availability.available).length)

const attentionClass = `text-${signalToneColor(signalToneEnum.ATTENTION)}`

const openCreated = () => webSiteStore.openSearchResultAsync({ type: config.value.resultType, id: props.message.draft_created_id, title: draft.value.name })
</script>

<template>
  <q-card flat bordered class="q-mt-sm">
    <q-card-section class="q-pa-sm">
      <div class="text-subtitle2">{{ draft.name }}</div>
      <div v-if="draft.company_name" class="text-caption">{{ draft.company_name }}</div>
      <div v-if="contact" class="text-caption">{{ contact }}</div>
      <div v-if="period" class="text-caption">{{ period }}</div>
      <div v-if="draft.kind === chatDraftKindEnum.PROJECT" class="text-caption">
        {{ i18n.t('common.service', 2) }}: {{ draft.services.length }}
        <span v-if="unavailableCount" :class="attentionClass">· {{ i18n.t('chat.unavailableServices', { count: unavailableCount }) }}</span>
      </div>
    </q-card-section>
    <q-card-actions align="right">
      <q-btn
        v-if="message.draft_created_id"
        flat
        color="positive"
        icon="mdi-check"
        :label="i18n.t(config.createdLabel)"
        @click="openCreated"
      />
      <q-btn
        v-else
        color="primary"
        :icon="config.icon"
        :label="i18n.t(config.openLabel)"
        @click="$emit('open', message)"
      />
    </q-card-actions>
  </q-card>
</template>
